import React, { useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Save, ShieldAlert, Loader2, BookOpenCheck, AlertTriangle, Wallet, RefreshCw, Search } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

/** Plagiarism provider configuration (iter-14).
 *
 *  Admin selects an active provider (Copyleaks / Turnitin / disabled) and pastes
 *  the credentials. Server stores raw keys in Mongo's app_config; GET endpoint
 *  only returns "configured: bool" flags + the non-secret bits (email, client_id,
 *  tenant_url), so we never re-render secrets back into the DOM.
 *
 *  Grammarly is intentionally separate — it is NOT a plagiarism provider; it's
 *  an optional grammar/style assistant pane wired into the editor.
 */
export default function PlagiarismProviderConfig({ cfg, onSaved }) {
  const initial = {
    provider:        cfg?.plagiarism_provider || '',
    scan_token_cost: cfg?.plagiarism_scan_token_cost ?? 1000,
    copyleaks_email: cfg?.copyleaks?.email || '',
    copyleaks_key:   '',
    turnitin_client_id:     cfg?.turnitin?.client_id || '',
    turnitin_client_secret: '',
    turnitin_tenant_url:    cfg?.turnitin?.tenant_url || '',
    // iter-16: Copyscape config
    copyscape_username: cfg?.copyscape?.username || '',
    copyscape_api_key: '',
    copyscape_test_mode: Boolean(cfg?.copyscape?.test_mode),
    copyscape_scan_token_cost: cfg?.copyscape_scan_token_cost ?? 1,
    grammarly_client_id: cfg?.grammarly_client_id || '',
    languagetool_endpoint: cfg?.languagetool_endpoint || '',
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  const copyleaksConfigured = Boolean(cfg?.copyleaks_configured);
  const turnitinConfigured  = Boolean(cfg?.turnitin_configured);
  const copyscapeConfigured = Boolean(cfg?.copyscape_configured);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        plagiarism_provider: form.provider,
        plagiarism_scan_token_cost: Math.max(0, parseInt(form.scan_token_cost || 0, 10)),
        copyscape_scan_token_cost: Math.max(0, parseInt(form.copyscape_scan_token_cost || 0, 10)),
        grammarly_client_id: form.grammarly_client_id,
        languagetool_endpoint: form.languagetool_endpoint,
      };
      // Only send the credential blocks that the admin actually typed into —
      // empty strings are a "don't change" sentinel handled server-side.
      if (form.copyleaks_email || form.copyleaks_key) {
        patch.copyleaks = { email: form.copyleaks_email, key: form.copyleaks_key };
      }
      if (form.turnitin_client_id || form.turnitin_client_secret || form.turnitin_tenant_url) {
        patch.turnitin = {
          client_id: form.turnitin_client_id,
          client_secret: form.turnitin_client_secret,
          tenant_url: form.turnitin_tenant_url,
        };
      }
      if (form.copyscape_username || form.copyscape_api_key || form.copyscape_test_mode !== Boolean(cfg?.copyscape?.test_mode)) {
        patch.copyscape = {
          username: form.copyscape_username,
          api_key: form.copyscape_api_key,
          test_mode: form.copyscape_test_mode,
        };
      }
      await adminApi.saveConfig(patch);
      toast.success('Plagiarism settings saved.');
      setForm({ ...form, copyleaks_key: '', turnitin_client_secret: '', copyscape_api_key: '' });
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save plagiarism settings.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="admin-plagiarism-card">
      <div className="flex items-start gap-2">
        <BookOpenCheck className="mt-0.5 h-4 w-4 text-primary" />
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">Plagiarism Provider</div>
          <div className="text-[11px] text-muted-foreground">
            Powers the in-editor &ldquo;Check for plagiarism&rdquo; pane. Wallet deduction
            happens server-side once the provider accepts the scan.
            {' '}
            {form.provider === 'copyleaks' && (copyleaksConfigured
              ? <span className="text-success">Copyleaks credentials configured.</span>
              : <span className="text-warning-foreground">Copyleaks credentials missing.</span>)}
            {form.provider === 'turnitin' && (turnitinConfigured
              ? <span className="text-success">Turnitin credentials configured.</span>
              : <span className="text-warning-foreground">Turnitin credentials missing.</span>)}
            {form.provider === 'copyscape' && (copyscapeConfigured
              ? <span className="text-success">Copyscape credentials configured (separate token wallet).</span>
              : <span className="text-warning-foreground">Copyscape credentials missing.</span>)}
          </div>
        </div>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <Label className="text-[11px]">Active provider</Label>
          <Select
            value={form.provider || 'disabled'}
            onValueChange={(v) => setForm({ ...form, provider: v === 'disabled' ? '' : v })}
          >
            <SelectTrigger className="h-8 mt-1 text-[12px]" data-testid="plagiarism-provider-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="disabled">Disabled (no scans)</SelectItem>
              <SelectItem value="copyleaks">Copyleaks (asynchronous)</SelectItem>
              <SelectItem value="copyscape">Copyscape (synchronous, separate wallet)</SelectItem>
              <SelectItem value="turnitin">Turnitin / iThenticate (enterprise stub)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Tokens charged per scan</Label>
          <Input
            type="number"
            min={0}
            max={1_000_000}
            value={form.scan_token_cost}
            onChange={(e) => setForm({ ...form, scan_token_cost: e.target.value })}
            className="mt-1 h-8 font-mono-stat text-[12px]"
            data-testid="plagiarism-token-cost-input"
          />
          <p className="mt-1 text-[10.5px] text-muted-foreground">
            Default 1,000 (≈ $0.02 of wallet credit at the current $/M tokens rate).
          </p>
        </div>
      </div>

      {/* ----- Copyleaks credentials ----- */}
      <div className="mt-4 rounded-sm border border-dashed border-border bg-muted/30 p-3">
        <div className="text-[12px] font-semibold">Copyleaks</div>
        <p className="text-[11px] text-muted-foreground">
          Get your key at <a href="https://api.copyleaks.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">api.copyleaks.com</a> → Dashboard → API Access.
        </p>
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          <div>
            <Label className="text-[11px]">Account email</Label>
            <Input
              type="email"
              value={form.copyleaks_email}
              onChange={(e) => setForm({ ...form, copyleaks_email: e.target.value })}
              placeholder="you@yourdomain.com"
              className="mt-1 h-8 text-[12px]"
              data-testid="plagiarism-copyleaks-email"
            />
          </div>
          <div>
            <Label className="text-[11px]">API key</Label>
            <Input
              type="password"
              value={form.copyleaks_key}
              onChange={(e) => setForm({ ...form, copyleaks_key: e.target.value })}
              placeholder={copyleaksConfigured ? '••••••• (paste a new one to replace)' : 'CL key'}
              className="mt-1 h-8 font-mono-stat text-[11.5px]"
              data-testid="plagiarism-copyleaks-key"
            />
          </div>
        </div>
      </div>

      {/* ----- Turnitin credentials (stub) ----- */}
      <div className="mt-3 rounded-sm border border-dashed border-border bg-muted/30 p-3">
        <div className="text-[12px] font-semibold flex items-center gap-1.5">
          Turnitin / iThenticate Core
          <span className="rounded-sm border border-warning/40 bg-warning/10 px-1.5 py-0 text-[10px] font-mono-stat text-warning-foreground">stub</span>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Sales-led onboarding via <a href="https://developers.turnitin.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">developers.turnitin.com</a>. Submission is currently a no-op placeholder while the institutional contract is signed.
        </p>
        <div className="mt-2 grid gap-2 md:grid-cols-3">
          <div>
            <Label className="text-[11px]">Client ID</Label>
            <Input
              value={form.turnitin_client_id}
              onChange={(e) => setForm({ ...form, turnitin_client_id: e.target.value })}
              placeholder="…"
              className="mt-1 h-8 font-mono-stat text-[11.5px]"
              data-testid="plagiarism-turnitin-client-id"
            />
          </div>
          <div>
            <Label className="text-[11px]">Client secret</Label>
            <Input
              type="password"
              value={form.turnitin_client_secret}
              onChange={(e) => setForm({ ...form, turnitin_client_secret: e.target.value })}
              placeholder={turnitinConfigured ? '••••••• (replace)' : '…'}
              className="mt-1 h-8 font-mono-stat text-[11.5px]"
              data-testid="plagiarism-turnitin-secret"
            />
          </div>
          <div>
            <Label className="text-[11px]">Tenant URL</Label>
            <Input
              value={form.turnitin_tenant_url}
              onChange={(e) => setForm({ ...form, turnitin_tenant_url: e.target.value })}
              placeholder="https://your-tenant.turnitin.com"
              className="mt-1 h-8 font-mono-stat text-[11.5px]"
              data-testid="plagiarism-turnitin-tenant"
            />
          </div>
        </div>
      </div>

      {/* ----- Copyscape credentials (iter-16) ----- */}
      <CopyscapeBlock
        form={form}
        setForm={setForm}
        configured={copyscapeConfigured}
        scanTokenCost={form.copyscape_scan_token_cost}
      />

      {/* ----- Grammar pane (NOT plagiarism) ----- */}
      <div className="mt-3 rounded-sm border border-dashed border-border bg-muted/30 p-3">
        <div className="text-[12px] font-semibold flex items-center gap-1.5">
          Grammar & Style (writer-facing)
        </div>
        <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
          <AlertTriangle className="mt-0.5 h-3 w-3 text-warning-foreground shrink-0" />
          Grammarly&apos;s public Text Editor SDK was sunset in early 2024 — leaving the field blank disables the in-editor grammar pane. We recommend running a self-hosted{' '}
          <a href="https://languagetool.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">LanguageTool</a> endpoint instead.
        </p>
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          <div>
            <Label className="text-[11px]">Grammarly clientId (legacy)</Label>
            <Input
              value={form.grammarly_client_id}
              onChange={(e) => setForm({ ...form, grammarly_client_id: e.target.value })}
              placeholder="client_xxxxxxxx"
              className="mt-1 h-8 font-mono-stat text-[11.5px]"
              data-testid="plagiarism-grammarly-id"
            />
          </div>
          <div>
            <Label className="text-[11px]">LanguageTool endpoint (preferred)</Label>
            <Input
              value={form.languagetool_endpoint}
              onChange={(e) => setForm({ ...form, languagetool_endpoint: e.target.value })}
              placeholder="https://api.languagetool.org/v2/check"
              className="mt-1 h-8 font-mono-stat text-[11.5px]"
              data-testid="plagiarism-languagetool-endpoint"
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="h-8 gap-1.5"
          data-testid="plagiarism-save-btn"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save plagiarism settings
        </Button>
        <span className="text-[10.5px] text-muted-foreground inline-flex items-center gap-1">
          <ShieldAlert className="h-3 w-3" /> Secrets are never read back from the server.
        </span>
      </div>
    </div>
  );
}



/** Copyscape sub-block — credentials + per-scan token price + balance probe.
 *  Kept in the same file so the parent's `form` state stays the source of
 *  truth (no prop drilling for the unified Save button). */
function CopyscapeBlock({ form, setForm, configured }) {
  const [balance, setBalance] = useState(null);
  const [probing, setProbing] = useState(false);

  const probeBalance = async () => {
    setProbing(true);
    try {
      const r = await adminApi.copyscapeBalance();
      setBalance(r.balance_usd);
      toast.success(`Copyscape pool: $${Number(r.balance_usd).toFixed(2)} remaining for ${r.username}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reach Copyscape.');
    } finally { setProbing(false); }
  };

  return (
    <div className="mt-3 rounded-sm border border-dashed border-border bg-muted/30 p-3" data-testid="plagiarism-copyscape-block">
      <div className="text-[12px] font-semibold flex items-center gap-1.5">
        <Search className="h-3.5 w-3.5 text-primary" /> Copyscape
        <span className="rounded-sm border border-primary/40 bg-primary/10 px-1.5 py-0 text-[10px] font-mono-stat text-primary">synchronous</span>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Scans complete in a single round-trip (no webhook). Get your API key at{' '}
        <a href="https://www.copyscape.com/api-guide.php" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">copyscape.com/api-guide.php</a>{' '}
        — fund the account with prepaid USD credit first. Each user is debited from the per-user Copyscape allowance below; the master pool funds the underlying paid API.
      </p>

      <div className="mt-2 grid gap-2 md:grid-cols-3">
        <div>
          <Label className="text-[11px]">Copyscape username</Label>
          <Input
            value={form.copyscape_username}
            onChange={(e) => setForm({ ...form, copyscape_username: e.target.value })}
            placeholder="quillandvine"
            className="mt-1 h-8 font-mono-stat text-[11.5px]"
            data-testid="plagiarism-copyscape-username"
          />
        </div>
        <div>
          <Label className="text-[11px]">API key</Label>
          <Input
            type="password"
            value={form.copyscape_api_key}
            onChange={(e) => setForm({ ...form, copyscape_api_key: e.target.value })}
            placeholder={configured ? '••••••• (replace)' : 'cs key'}
            className="mt-1 h-8 font-mono-stat text-[11.5px]"
            data-testid="plagiarism-copyscape-key"
          />
        </div>
        <div>
          <Label className="text-[11px]">Tokens per Copyscape scan</Label>
          <Input
            type="number"
            min={0}
            max={10_000}
            value={form.copyscape_scan_token_cost}
            onChange={(e) => setForm({ ...form, copyscape_scan_token_cost: e.target.value })}
            className="mt-1 h-8 font-mono-stat text-[12px]"
            data-testid="plagiarism-copyscape-token-cost"
          />
        </div>
      </div>

      <div className="mt-2 flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Switch
            checked={Boolean(form.copyscape_test_mode)}
            onCheckedChange={(v) => setForm({ ...form, copyscape_test_mode: v })}
            data-testid="plagiarism-copyscape-test-mode"
          />
          <Label className="text-[11px] text-muted-foreground">Test mode (no credit consumed)</Label>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={probeBalance}
          disabled={probing || !configured}
          className="ml-auto h-7 gap-1.5 px-2 text-[11px]"
          data-testid="plagiarism-copyscape-balance-btn"
          title={configured ? 'Probe live Copyscape balance' : 'Save credentials first'}
        >
          {probing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wallet className="h-3.5 w-3.5" />}
          {balance !== null ? `Pool: $${Number(balance).toFixed(2)}` : 'Check pool balance'}
        </Button>
      </div>
    </div>
  );
}
